import { getInstallerDashboardFundedProjects } from '../services/salesforce';
import { Contact, DashboardFundedProject } from '../../../../types/api';
import {
  FP_EXTERNAL_STATUSES,
  MILESTONE_SHORT_TO_LONG,
  PROJECT_MILESTONE_ORDER,
} from '../constants';
import useAuth from './useAuth';
import { QueryClient, useQuery } from 'react-query';

export const PROJECTS_QUERY_KEY = 'projects';

type ProjectsQueryData = {
  active: DashboardFundedProject[];
  changeOrder: DashboardFundedProject[];
  completed: DashboardFundedProject[];
  closed: DashboardFundedProject[];
  milestones: string[];
};

// Patch nickname on the project whose loan application matches; leave other rows unchanged.
function withUpdatedProjectNickname(
  projects: DashboardFundedProject[],
  applicationId: string,
  projectNickname: string | null
): DashboardFundedProject[] {
  return projects.map((project) =>
    String(project.loan_application?.id) === String(applicationId)
      ? {
          ...project,
          loan_application: {
            ...project.loan_application,
            project_nickname: projectNickname,
          },
        }
      : project
  );
}

/** Keep installer dashboard project rows in sync after a nickname PATCH. */
export function updateProjectNicknameInProjectsCache(
  queryClient: QueryClient,
  applicationId: string,
  projectNickname: string | null
): void {
  // Dashboard useProjects shares the 'projects' key prefix with detail/paginated
  // queries — only update buckets that look like installer dashboard data.
  queryClient.setQueriesData<ProjectsQueryData | undefined>(
    [PROJECTS_QUERY_KEY],
    (old) => {
      if (!old || !Array.isArray(old.active)) {
        return old;
      }
      return {
        ...old,
        active: withUpdatedProjectNickname(
          old.active,
          applicationId,
          projectNickname
        ),
        changeOrder: withUpdatedProjectNickname(
          old.changeOrder,
          applicationId,
          projectNickname
        ),
        completed: withUpdatedProjectNickname(
          old.completed,
          applicationId,
          projectNickname
        ),
        closed: withUpdatedProjectNickname(
          old.closed,
          applicationId,
          projectNickname
        ),
      };
    }
  );
}

const fetchProjects = async (user?: Contact | null) => {
  if (!user?.account?.id || !user?.account?.type) {
    throw new Error('No account ID or type found');
  }

  try {
    const response = await getInstallerDashboardFundedProjects(
      user.account.id,
      user.account.type
    );

    const active: DashboardFundedProject[] = [];
    const changeOrder: DashboardFundedProject[] = [];
    const completed: DashboardFundedProject[] = [];
    const closed: DashboardFundedProject[] = [];
    const milestones: string[] = [];

    for (const proj of response) {
      switch (proj.external_status) {
        case FP_EXTERNAL_STATUSES.IN_PROGRESS:
          active.push(proj);
          if (proj.active_milestone) {
            const milestone = MILESTONE_SHORT_TO_LONG[proj.active_milestone];
            if (!milestones.includes(milestone)) {
              milestones.push(milestone);
            }
          }
          break;
        case FP_EXTERNAL_STATUSES.PENDING_CHANGE_ORDER:
          changeOrder.push(proj);
          break;
        case FP_EXTERNAL_STATUSES.FULLY_FUNDED:
          completed.push(proj);
          break;
        case FP_EXTERNAL_STATUSES.CLOSED:
          closed.push(proj);
          break;
      }
    }

    milestones.sort((a, b) => {
      const numberA = PROJECT_MILESTONE_ORDER.get(a) ?? 1000;
      const numberB = PROJECT_MILESTONE_ORDER.get(b) ?? 1000;
      return numberA - numberB;
    });

    return {
      active,
      changeOrder,
      completed,
      closed,
      milestones,
    };
  } catch (error) {
    throw new Error(
      error instanceof Error ? error.message : 'An unknown error occurred'
    );
  }
};

export function useProjects() {
  const { user } = useAuth();
  const query = useQuery(
    [PROJECTS_QUERY_KEY, user?.account?.id, user?.account?.type],
    () => fetchProjects(user),
    {
      enabled: !!user?.account?.id && !!user?.account?.type,
    }
  );
  return {
    ...query,
    data: query.data ?? {
      active: [],
      changeOrder: [],
      completed: [],
      closed: [],
      milestones: [],
    },
    totalProjects:
      (query.data?.active?.length ?? 0) +
      (query.data?.closed?.length ?? 0) +
      (query.data?.completed?.length ?? 0) +
      (query.data?.changeOrder?.length ?? 0),
  };
}
